import { AttachmentFile, DocumentTableCell, DocumentWord, IWordWithConfidence, Region, TemplateCell } from "../Azure";
import { IUploadedFileProperties } from "../EngineUtils";
import { IQuantityFormula } from "./EngineSharedUtils";

export enum TPARuleTypes {
	RemoveComplement = "Remove Complement",
	ConvertToLikelyComplement = "Convert to likely complement",
	AdditionalComplements = "Additional complements",
	SplitUpLineItems = "Split up Line Items",
	RolledFlooringWasteCalculation = "Rolled Flooring Waste Calculation",
	AlternativeMaximum = "Alternative maximum",
	AlternateXactimateCoding = "Alternate Xactimate coding",
}

export const gTPARuleTypes = Object.values(TPARuleTypes);

export interface IEstimaticsData {
	estimateId: string;
	projectName: string;
	estimateReferenceNumber: string;
	requesterName: string;
	email: string;
	phone: string;
	propertyAddress: string;
	insuranceCarrier: string;
	claimNumber: string;
	tpa: string;
	dateCreated: string;
	dateContacted: string;
	dateOfLoss: string;
	office: string;
	assignee: string;
	status: string;
	approximateClaim: string;
	links: string[];
	notes: string[];
	files: AttachmentFile[];
	category: string;
	typeOfLoss: string;
	fieldData: string;
	deleted: boolean;
	xactimateTransactionId?: string;
	uploadImagesFailed?: boolean;
	measurementData: any[];
	propertyAddressComponents: string;
	uploadedFilesProperties?: IUploadedFileProperties[];
	autoMergeRoomCompleted: boolean;
}
export interface IEstimateDataForUI {
	extractedImage?: IImageMetadata;
	estimateQueueItem?: IQueuedItemToProcess;
	metadata?: IEstimateMetadata[];
	dataForUI: IDocumentForUI;
}
export type TCellWithInputMap = { [s: string]: ITupleContentById | IFreeFormContentById };
export interface IMetadataForFinalEstimate {
	pageIndex?: number;
	roomName: string;
	pageId: string;
	pageName: string;
	sourceImageName: string;
	uploadId: string;
	fullPageImagePath: string;
	originalName: string;
	mergeWith?: string;
	uploadIdArray?: string[];
	fullImagePathArray?: string[];
	sourceImageNameArray?: string[];
	roomTypes?: RoomType[];
}

export enum RoomType {
	STORAGE = "STORAGE",
	STAIRS = "STAIRS",
	PATIO__DECK = "PATIO/DECK",
	OFFICE = "OFFICE",
	MECHANICAL = "MECHANICAL",
	LAUNDRY = "LAUNDRY",
	KITCHEN = "KITCHEN",
	HALLWAY = "HALLWAY",
	GYM = "GYM",
	OTHER = "OTHER",
	GARAGE = "GARAGE",
	FAMILY_ROOM__LIVING_ROOM__DEN = "FAMILY ROOM/LIVING ROOM/DEN",
	EXTERIOR__ROOF = "EXTERIOR/ROOF",
	ENTRY__FOYER = "ENTRY/FOYER",
	DINING_ROOM = "DINING ROOM",
	CRAWL_SPACE = "CRAWL SPACE",
	CLOSET = "CLOSET",
	BEDROOM = "BEDROOM",
	BATHROOM = "BATHROOM",
	BASEMENT = "BASEMENT",
	ATTIC = "ATTIC",
}

export interface IDocumentForUI {
	// documentMetadata: IDocumentMetadata, // TODO ::
	metadata: IMetadataForFinalEstimate | null;
	itemForDebugOnly: IQueuedItemToProcess | null; // TODO :: remove when all the debug is done
	page: IPageForUI;
	roomName: string;
	originalName: string;
}
export interface IPageForUI {
	// pageMetadata: IPageMetadata, // TODO ::
	tuplesOutput: ITupleOutputForUI[];
	fullPageImagePath: string;
	mergeWith?: string;
}

export const FileErrorDummyPath = "~";

export interface IUploadFileData {
	mime: string;
	originalFileName: string;
	storagePath: string;
	uploadId?: string;
	uploadIndex?: number;
	deleted?: boolean;
	postUploadResults?: any[]; // this is used only for the UI. It's not saved in the database
}

export enum ESkipProcessingReason {
	NotTicSheet = "not-tic-sheet",
	UnsupportedTicType = "unsupported-tic-type",
	BadDocumentAngle = "bad-document-angle",
	NoCellsWithInput = "no-cells-with-input",
}
export interface ITicSheetHeaderInfo {
	address: string;
	roomName: string;
	dashNumber: string;
	debris: string[];
}
export interface IImageMetadata {
	id: string;
	estimatePath?: string;
	matchingRowsPath?: string;
	storagePath: string;
	originalName: string;
	classification?: string;
	mime: string;
	extractedFrom: string;
	layoutStoragePath?: string;
	readStoragePath?: string;
	cellsWithInputPath?: string;
	cellsRawDataPath?: string;
	skipProcessingReason?: ESkipProcessingReason;
	finalEstimatePath?: string;
	complementLineItemsPath?: string;
	ticInfo?: ITicSheetHeaderInfo;
	dataForUIPath?: string;
	pageId?: string;
	freeFormCellsPath?: string;
}
export interface IQueuedItemToProcess {
	organizationId: string;
	itemId: string;
	estimateId: string;
	tpa?: string;
	carrier?: string;
	originalUploadedFiles: IUploadFileData[];
	extractedImages?: IImageMetadata[];
	queueEntryTime: number;
	startProcessTime: number;
	endProcessTime: number;
	status: number;
	isParent?: boolean;

	processorId: string;
	creatorId: string;
	lastUpdatedBy: string;
	isTest?: boolean;
	useCache?: boolean;
	pagesToProcess?: number[];
	itemIndex?: number;
}

export class EExtraDataMappingType {
	// errors - Make sure to add the type to the isError function when adding a new error type
	static readonly NoEmptyValueNoDefault = "no-empty-value-no-default";
	static readonly NotAllMappingsUsed = "not-all-mappings-used";
	static readonly InputWithNoMapping = "input-with-no-mapping";
	static readonly QuantityOutOfRange = "quantity-out-of-range";
	static readonly QuantityOutOfRangeTPARule = "quantity-out-of-range-tpa-rule";
	static readonly LowConfidenceInput = "low-confidence-input";
	static isError(type: string): boolean {
		return (
			type === EExtraDataMappingType.NoEmptyValueNoDefault ||
			type === EExtraDataMappingType.NotAllMappingsUsed ||
			type === EExtraDataMappingType.InputWithNoMapping ||
			type === EExtraDataMappingType.LowConfidenceInput ||
			type === EExtraDataMappingType.QuantityOutOfRange ||
			type === EExtraDataMappingType.QuantityOutOfRangeTPARule
		);
	}

	// modifers
	static readonly QuantityModifier = "quantity-modifier";
}

export interface IMappingExtraData {
	rnrIndex?: number;
	rnrType?: string;
	type?: string;
	variablesMapping?: VariableMapping[];
	element?: LineItemElementProps;
	mappings?: IWordMapping[];
}

export type LineItemStringPartOrMappingExtraData = string | IMappingExtraData;

export interface ILineItemMetadata {
	lineItem: IDatabaseLineItem;
	variablesMapping: IVariableMappingMini[];
	leftWordsMapping: IWordMapping[];
	rightWordsMapping: IWordMapping[];
	outLineItems: LineItemStringPartOrMappingExtraData[][];
}
export interface IEstimateMetadata {
	leftWordsMappingArray: IWordMapping[][];
	rightWordsMappingArray: IWordMapping[][];
	allExistingMappingArray: string[][];
	tupleId: string;
	tupleContent: ITupleContentById;
	databaseMapping: IDatabaseMappingMini;
	leftWordsAfterCleanup: IWordWithConfidence[];
	rightWordsAfterCleanup: IWordWithConfidence[];
	leftWordsParameterised: IWordParameterized[];
	rightWordsParameterised: IWordParameterized[];
	lineItemsMetadata: ILineItemMetadata[];
	allUnusedWordsParameterised?: IWordParameterized[];
	lowConfidenceArray?: DocumentWord[];
}

export interface ILineItemForUI {
	isComplement?: boolean;
	$$complementTupleId?: string; // we use this to store the original tuple id for this line item when we merge it under one parent in the UI
	addedByTPARule?: boolean;
	deleted?: boolean;
	lineItem: string;
	lineItemId: string;
	qnm: IQuantityMeasurement[];
	formula?: IQuantityFormula;
	mappingExtraData: IMappingExtraData[];
	leftMapping: IWordWithConfidence[];
	rightMapping: IWordWithConfidence[];
	confirmed?: boolean;
	confidence?: number | null;
	orderIndex?: number;
}
export interface IMissingMappings {
	lineItem: string;
	lineItemId: string;
	room: string;
	unit: string;
	calc: string;
}

export interface ITupleOutputForUI {
	isComplementOf?: string;
	isLikely?: boolean;
	lowConfidenceArray?: DocumentWord[];
	originalTupleIndex?: number;
	tupleId: string;
	lineItems: ILineItemForUI[];
	fullPageImagePath?: string;
	tupleRegion: Region;
	hasError?: boolean;
	hadErrorThatHasBeenFixed?: boolean;
	deleted?: boolean;
	originalInput?: {
		left: IWordWithConfidence[];
		right: IWordWithConfidence[];
		selections: string[];
	};
	orederIndex?: number;
	isFreeFormFlag?: boolean;
}

export interface IWordParameterized {
	confidence?: number;
	parameter: string;
	word: string;
	id?: number;
}

export interface IWordMapping extends IWordParameterized {
	mapping?: IVariableMappingMini;
	usedInCreation?: boolean;
}

export interface IAcceptedValue extends IWordMapping {
	value: string;
}

export interface IQuantityMeasurement {
	error?: string;
	measurement: string;
	quantity: number;
	modifier?: string;
}

export interface ITupleContentById {
	id: string;
	selectedWords: string[];
	azureLeftWords: DocumentWord[];
	azureRightWords: DocumentWord[];

	templateTupleIndex: number;
	azureLeftCell: DocumentTableCell;
	azureRightCell: DocumentTableCell;
	templateLeftCell: TemplateCell;
	templateRightCell: TemplateCell;
	// templateTuple: TemplateCellTuple;
	azureLeftRegion?: Region;
	azureRightRegion?: Region;
	azureFullRegion?: Region;
	azureLeftRegionBase64?: string;
	azureRightRegionBase64?: string;
	azureFullRegionBase64?: string;
}

/**
 * Very similar to ITupleContentById but doesn't have Template values
 * (Template doesn't exist for free form content)
 */
export interface IFreeFormContentById {
	id: string;
	selectedWords: string[];
	azureLeftWords: DocumentWord[];
	azureRightWords: DocumentWord[];

	azureLeftCell: DocumentTableCell;
	azureRightCell: DocumentTableCell;

	azureLeftRegion?: Region;
	azureRightRegion?: Region;
	azureFullRegion?: Region;
	azureLeftRegionBase64?: string;
	azureRightRegionBase64?: string;
	azureFullRegionBase64?: string;
}

export enum QueuedItemToProcessStatus {
	None = 0,
	Queued = 10,
	Extracted = 20, // images extraction from pdf
	Classified = 30, // images classification and orientation fix
	LayoutAndReadExtracted = 40, //Azure layout and text reading
	CellsExtracted = 50, //cell extraction from layout and reading
	RawDataFromCellsExtracted = 60, //extract raw data from cells
	CellWithInputExtracted = 70, // Identify and extract cells with input
	CellImproveWithOpenAICompleted = 80, // use openAI to improve content extraction ( selection box etc )
	LineItemDatabaseCreated = 90, // activate mapping algorithm on cells and create a line items metadata database
	ComplementLineItemsCreated = 91, // create complement line items
	UILineItemDatabaseCreated = 93, // convert line items metadata database to UI friendly format
	FinalEstimateCreated = 97, // create final estimate
	Completed = 100, //'100-completed',
	CompletedNotProcessed = 110, // '110-completed-not-processed',
	CompletedWithError = 120, //'120-completed-with-error',
}

export function getQueuedItemToProcessStatusName(status: QueuedItemToProcessStatus): string {
	switch (status) {
		case QueuedItemToProcessStatus.None:
			return "None";
		case QueuedItemToProcessStatus.Queued:
			return "Queued";
		case QueuedItemToProcessStatus.Extracted:
			return "Extracted";
		case QueuedItemToProcessStatus.Classified:
			return "Classified";
		case QueuedItemToProcessStatus.LayoutAndReadExtracted:
			return "LayoutAndReadExtracted";
		case QueuedItemToProcessStatus.CellsExtracted:
			return "CellsExtracted";
		case QueuedItemToProcessStatus.RawDataFromCellsExtracted:
			return "RawDataFromCellsExtracted";
		case QueuedItemToProcessStatus.CellWithInputExtracted:
			return "CellWithInputExtracted";
		case QueuedItemToProcessStatus.CellImproveWithOpenAICompleted:
			return "CellImproveWithOpenAICompleted";
		case QueuedItemToProcessStatus.LineItemDatabaseCreated:
			return "LineItemDatabaseCreated";
		case QueuedItemToProcessStatus.Completed:
			return "Completed";
		case QueuedItemToProcessStatus.CompletedNotProcessed:
			return "CompletedNotProcessed";
		case QueuedItemToProcessStatus.CompletedWithError:
			return "CompletedWithError";
		case QueuedItemToProcessStatus.UILineItemDatabaseCreated:
			return "UILineItemDatabaseCreated";
		case QueuedItemToProcessStatus.FinalEstimateCreated:
			return "FinalEstimateCreated";
	}
	return "Unknown";
}

export interface LineItemElementProps {
	value: string;
	type: "text" | "variable";
	acceptableValues?: string[];
	allowEmptyValue?: boolean;
	conditional?: boolean;
	defaultValue?: string;
}
export interface LineItemVariable extends LineItemElementProps {
	acceptableValues: string[]; // make non optional
}

export interface IDatabaseLineItem {
	category?: string;
	elements: LineItemElementProps[];
	originalLineItem: string;
	id: string;
	deleted?: boolean;
	deletedBy?: string;
	deletedTimestamp?: number;
	lastModifiedBy?: string;
	lastModifiedAt?: number;
	index?: number;
	createTime?: number;
	scopes?: string[];
}
export type AIMatchings = { [s: string]: IAIMatch };
export interface IAIMatch {
	variable?: string;
	confidence: "confident" | "not confident";
}
export interface VariableMapping {
	// aiMatch: IAIMatch;
	accepted: boolean;
	deleted: boolean;
	dotColor: string;

	variableValue?: string;
	extraVariableValues?: string[];
	quantityType?: string;
	quantityValue?: string;
	measurement?: string;
	ticInput: string;
}
// Used outside the tools
export interface IVariableMappingMini {
	variableValue?: string;
	extraVariableValues?: string[];
	quantityType?: string;
	quantityValue?: string;
	measurement?: string;
	ticInput: string;
}

export enum EExtraVariableValues {
	NotAvailable = "not available",
	TicSheetError = "Mark as tic sheet error",
	CreateNewVariable = "Add new variable",
	UseLineMarker = "Use Line Marker",
	AcceptVariableDependency = "Accept Dependency",
	AcceptInvertedVariableDependency = "Accept Inverted Dependency",
}
export declare type VariableMappingMap = { [key: string]: VariableMapping[] };
export declare type VariableMappingMiniMap = {
	[key: string]: IVariableMappingMini[];
};
export declare type AIMatchingsMap = { [key: string]: AIMatchings };

export interface IComplementLineItemRule {
	maxQuantity?: number;
	minQuantity?: number;
	selectedVariabls?: { name: string; index: number }[];
	exclusionSelectedVariabls?: { name: string; index: number }[];
	targetVariables?: { name: string; index: number; elementIndex?: number }[];
	isLikely?: boolean;
	isSpecialTPAActivationComplements?: boolean;
	defaultMeasurement?: string;
	defaultQuantity?: number;
	confidence?: number;
}
export type ComplementLineItemRulesMap = { [key: string]: IComplementLineItemRule };
export type ComplementLineItemRulesMapNew = { [key: string]: IComplementLineItemRule[] };
export interface IDatabaseMapping {
	minQuantity?: { [s: string]: number };
	maxQuantity?: { [s: string]: number };
	overrideDefaultValues?: { [s: string]: string };
	defaultQuantity?: number;
	defaultMeasurement?: string;
	variableDependency?: ("dependent" | "inverted" | "independent")[];
	notes: string;
	variablesMapping: VariableMappingMap;
	complementLineProperties?: ComplementLineItemRulesMap;
	complementLinePropertiesNew: ComplementLineItemRulesMapNew;
	selectedLineItemsID: string[];
	selectedLineItems?: IDatabaseLineItem[];

	selectedComplementLineItemsID: string[];
	selectedComplementLineItems?: IDatabaseLineItem[];
	tupleID: string;
	// aiMatchings: AIMatchingsMap;
	ticType: string;
	state: "new" | "verified" | "unverified";
	badScans?: string[];
	allowedText?: string[];
}
export interface IAlternativeMaximum {
	tupleID: string;
	maxQuantity: number;
	measurement: string;
	explanation: string;
}
export interface IXactimateAlternativeMapping {
	source: string;
	dest: string;
}
export interface ITPARule {
	tpa: string;
	carrier: string;
	ruleType: string;
	rules: {
		alternativeMaximums: IAlternativeMaximum[];
		alternativeXAMappings: IXactimateAlternativeMapping[];
		rollupType: string;
		rollupPercentage: number;
		rollupApplied: string[];
		selectedTuples: string[];
		selectedComplementLineItems: string[];
		selectedComplementLineItemsRules: string[];
		expandedTupleIDS: string[];
		splitLineItems: string[];
	};
}

// Used outside the tools
export interface IDatabaseMappingMini {
	overrideDefaultValues?: { [s: string]: string };
	minQuantity?: { [s: string]: number };
	maxQuantity?: { [s: string]: number };
	defaultQuantity?: number;
	defaultMeasurement?: string;
	variableDependency?: ("dependent" | "inverted" | "independent")[];
	complementLineRules?: ComplementLineItemRulesMapNew;
	variablesMapping: VariableMappingMiniMap;
	selectedLineItemsID: string[];
	selectedLineItems: IDatabaseLineItem[];
	selectedComplementLineItemsID: string[];
	selectedComplementLineItems: IDatabaseLineItem[];
	tupleID: string;
	ticType: string;
	badScans?: string[];
	allowedText?: string[];
}

export enum ScopeTag {
	water_mitigation = "water_mitigation",
	interior_reconstruction = "interior_reconstruction",
	exterior_reconstruction = "exterior_reconstruction",
	contents = "contents",
	mold = "mold",
	fire_mitigation = "fire_mitigation",
	biohazard = "biohazard",
	time_and_material = "time_and_material",
}